import { useTreeViewOutletContext } from "@/components/layout/TreeViewOutletContext";
import { useAuth } from "@/features/auth/hooks/useAuth";
import {
  setPendingChatAttachments,
  type PendingChatAttachment,
} from "@/features/chat/store/pendingAttachments";
import {
  createClipboardFolder,
  deleteDocument,
  downloadDocument,
  exportClipboard,
  normalizeClipboardFolderName,
} from "@/features/clipboard/api/clipboard";
import {
  invalidateClipboardAgentSearches,
  removeDocsFromClipboardCache,
  useClipboardAgentSearchQuery,
  useClipboardTreeQuery,
  useInvalidateClipboard,
} from "@/features/clipboard/clipboardQueries";
import { ClipboardCreateFolderDialog } from "@/features/clipboard/components/ClipboardCreateFolderDialog";
import {
  ClipboardFilesPane,
  type ClipboardUploadActions,
} from "@/features/clipboard/components/ClipboardFilesPane";
import { ClipboardNavBar } from "@/features/clipboard/components/ClipboardNavBar";
import { ClipboardPageHeader } from "@/features/clipboard/components/ClipboardPageHeader";
import {
  ClipboardAskBar,
  ClipboardRecentSearches,
} from "@/features/clipboard/components/ClipboardSearchBar";
import { ConfirmDialog, type ConfirmRequest } from "@/features/clipboard/components/ConfirmDialog";
import {
  ClipboardSelectionProvider,
  useClipboardSelection,
} from "@/features/clipboard/context/ClipboardSelectionContext";
import { useClipboardOpenPreview } from "@/features/clipboard/hooks/useClipboardOpenPreview";
import { useClipboardRoute } from "@/features/clipboard/hooks/useClipboardRoute";
import { clipboardFolderUrl } from "@/features/clipboard/utils/urls";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

const PURGE_NOTE = (
  <>
    This removes the file, extracted text, and search indexes from Paperclip’s
    live systems, and it can’t be recovered through Paperclip. Encrypted
    provider backups may persist temporarily under their retention policies.
  </>
);

const ClipboardPageBody = () => {
  const { user } = useAuth();
  const { path, sharedOwnerId, routeSearchId } = useClipboardRoute();
  const { selected, selectedFolders, clearSelection } = useClipboardSelection();
  const { setClipboardActions } = useTreeViewOutletContext();
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<ConfirmRequest | null>(null);
  const [exporting, setExporting] = useState(false);
  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [folderError, setFolderError] = useState<string | null>(null);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const navigate = useNavigate();
  const invalidateClipboard = useInvalidateClipboard();
  const queryClient = useQueryClient();
  const uploadActionsRef = useRef<ClipboardUploadActions | null>(null);
  const openPreview = useClipboardOpenPreview(setError);

  const { data: tree, error: treeError } = useClipboardTreeQuery(
    path,
    sharedOwnerId,
    !!user,
  );
  const activeSearchQuery = useClipboardAgentSearchQuery(routeSearchId, !!user);
  const activeSearch = activeSearchQuery.data;

  useEffect(() => {
    if (
      activeSearch &&
      ["complete", "failed", "cancelled"].includes(activeSearch.status)
    ) {
      void invalidateClipboardAgentSearches(queryClient);
    }
  }, [activeSearch?.status, queryClient]);

  const fetchError = treeError instanceof Error ? treeError.message : null;
  const searchFetchError =
    activeSearchQuery.error instanceof Error ? activeSearchQuery.error.message : null;
  const displayError = error ?? searchFetchError ?? fetchError;

  const requestBulkDelete = useCallback(() => {
    if (!user || selected.size === 0) return;
    const ids = [...selected];
    setConfirm({
      title: `Delete ${ids.length} file${ids.length > 1 ? "s" : ""}?`,
      description: PURGE_NOTE,
      confirmLabel: `Delete ${ids.length} permanently`,
      onConfirm: () => {
        clearSelection();
        const rollback = removeDocsFromClipboardCache(queryClient, ids);
        Promise.allSettled(ids.map((id) => deleteDocument(id))).then((results) => {
          const failed = results.filter((r) => r.status === "rejected").length;
          if (failed > 0) {
            rollback();
            invalidateClipboard();
            setError(
              `${ids.length - failed} deleted, ${failed} could not be deleted. Please retry.`,
            );
          } else {
            invalidateClipboard();
          }
        });
      },
    });
  }, [clearSelection, invalidateClipboard, queryClient, selected, user]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    setError(null);
    try {
      const ids =
        selected.size > 0
          ? [...selected]
          : activeSearch
            ? activeSearch.documents.map((document) => document.id)
            : undefined;
      await exportClipboard(path, ids);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Export failed");
    } finally {
      setExporting(false);
    }
  }, [activeSearch, path, selected]);

  const selectionCount = selected.size + selectedFolders.size;

  const handleStartChat = useCallback(() => {
    const availableDocuments = activeSearch?.documents ?? tree?.documents;
    if (!availableDocuments || selectionCount === 0) return;
    const docsById = new Map(availableDocuments.map((d) => [d.id, d]));
    const items: PendingChatAttachment[] = [];
    for (const id of selected) {
      const d = docsById.get(id);
      if (!d) continue;
      const shortId = `usr_${d.id.replace(/-/g, "").slice(0, 12)}`;
      const documentPath = d.folder_path ?? path;
      items.push({
        kind: "file",
        path: documentPath
          ? `/clipboard/${documentPath}/${shortId}`
          : `/clipboard/${shortId}`,
        name: d.filename,
      });
    }
    for (const name of selectedFolders) {
      items.push({
        kind: "folder",
        path: path ? `/clipboard/${path}/${name}/` : `/clipboard/${name}/`,
        name,
      });
    }
    if (items.length === 0) return;
    setPendingChatAttachments(items);
    window.open("/chat", "_blank", "noopener");
  }, [activeSearch, tree, selectionCount, selected, selectedFolders, path]);

  const openCreateFolder = useCallback(() => {
    setFolderError(null);
    setNewFolderName("");
    setCreateFolderOpen(true);
  }, []);

  const handleCreateFolder = async (e: React.FormEvent) => {
    e.preventDefault();
    setFolderError(null);
    if (!newFolderName.trim()) {
      setFolderError("Name is required.");
      return;
    }
    if (!user) {
      setFolderError("Sign in to create a folder.");
      return;
    }
    setCreatingFolder(true);
    try {
      const normalized = normalizeClipboardFolderName(newFolderName);
      await createClipboardFolder(normalized);
      invalidateClipboard();
      setCreateFolderOpen(false);
      setNewFolderName("");
      clearSelection();
      navigate(clipboardFolderUrl(normalized));
    } catch (err) {
      setFolderError(err instanceof Error ? err.message : "Failed to create folder");
    } finally {
      setCreatingFolder(false);
    }
  };

  const hasContent = activeSearch
    ? activeSearch.documents.length > 0 || (activeSearch.folders?.length ?? 0) > 0
    : !!tree && (tree.documents.length > 0 || tree.folders.length > 0);
  const canModify = !sharedOwnerId;

  useEffect(() => {
    setClipboardActions({
      selectionCount,
      selectedDocCount: selected.size,
      hasContent,
      canModify,
      isExporting: exporting,
      onUploadFiles: () => uploadActionsRef.current?.onUploadFiles(),
      onUploadFolder: () => uploadActionsRef.current?.onUploadFolder(),
      onCreateFolder: openCreateFolder,
      onImportFiles: (files) => uploadActionsRef.current?.onImportFiles(files),
      onStartChat: handleStartChat,
      onExport: () => void handleExport(),
      onBulkDelete: requestBulkDelete,
      onView: (doc) => {
        if (doc) void openPreview(doc);
      },
      onViewOriginal: (doc) => {
        if (doc) void openPreview(doc, "raw");
      },
      onDownload: (doc) => {
        if (!doc) return;
        void downloadDocument(doc.id, doc.filename).catch((err) =>
          setError(err instanceof Error ? err.message : "Download failed"),
        );
      },
    });
    return () => setClipboardActions(null);
  }, [
    setClipboardActions,
    selectionCount,
    selected.size,
    hasContent,
    canModify,
    exporting,
    handleStartChat,
    handleExport,
    requestBulkDelete,
    openPreview,
    openCreateFolder,
  ]);

  return (
    <div className="flex min-h-full min-w-0 flex-col bg-white">
      <ClipboardPageHeader />

      {displayError && (
        <div
          role="alert"
          className="mx-8 mt-5 flex items-baseline justify-between gap-4 border-l-2 border-red-700 pl-4 text-sm leading-6 text-red-800"
        >
          <span>{displayError}</span>
          <button
            type="button"
            onClick={() => setError(null)}
            aria-label="Dismiss error"
            className="shrink-0 text-zinc-400 hover:text-zinc-900"
          >
            Dismiss
          </button>
        </div>
      )}

      <div className="shrink-0 space-y-3 px-8 pt-5 pb-3">
        <ClipboardRecentSearches onError={setError} />
        <ClipboardNavBar />
      </div>

      <ClipboardFilesPane onError={setError} uploadActionsRef={uploadActionsRef} />

      {/* Spacer keeps the list from hiding under the docked composer. */}
      <div className="min-h-6 flex-1" aria-hidden />
      <ClipboardAskBar onError={setError} />

      <ClipboardCreateFolderDialog
        open={createFolderOpen}
        creating={creatingFolder}
        folderName={newFolderName}
        folderError={folderError}
        canSubmit={!!user}
        onOpenChange={setCreateFolderOpen}
        onFolderNameChange={setNewFolderName}
        onSubmit={handleCreateFolder}
      />
      <ConfirmDialog request={confirm} onClose={() => setConfirm(null)} />
    </div>
  );
};

export const ClipboardPage = () => {
  useDocumentTitle("Clipboard");
  const { user } = useAuth();
  const { setInspectedDoc } = useTreeViewOutletContext();

  const onViewChange = useCallback(() => {
    setInspectedDoc(null);
  }, [setInspectedDoc]);

  if (!user) return null;

  return (
    <ClipboardSelectionProvider onViewChange={onViewChange}>
      <ClipboardPageBody />
    </ClipboardSelectionProvider>
  );
};
